import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["bookmarkIcon", "compareIcon", "compareText"]

  connect() {
    this.doctorId = this.element.dataset.doctorId
    this.hospitalId = this.element.dataset.hospitalId
    this.favoritableType = this.element.dataset.favoritableType || (this.hospitalId ? "Hospital" : "Doctor")
    this.updateBookmarkState()
    this.updateCompareState()
    this._onCompareUpdate = () => this.updateCompareState()
    window.addEventListener("compare:updated", this._onCompareUpdate)
  }

  disconnect() {
    if (this._onCompareUpdate) window.removeEventListener("compare:updated", this._onCompareUpdate)
  }

  get storageKey() {
    return this.favoritableType === "Hospital" ? "medifyr_compare_hospitals" : "medifyr_compare_doctors"
  }

  compareData() {
    const d = this.element.dataset
    if (this.favoritableType === "Hospital") {
      return {
        id: this.doctorId || this.hospitalId,
        name: d.mapDoctorName || "",
        specialty: d.mapDoctorSpecialty || "Multispecialty Hospital",
        hospital: d.mapDoctorHospital || "",
        photo: d.mapDoctorPhoto || "",
        url: d.mapDoctorUrl || "#",
        fee: d.mapDoctorFee || "",
        rating: d.mapDoctorRating || "",
        experience: d.mapDoctorExperience || ""
      }
    } else {
      return {
        id: this.doctorId,
        name: d.mapDoctorName || "",
        specialty: d.mapDoctorSpecialty || "",
        hospital: d.mapDoctorHospital || "",
        photo: d.mapDoctorPhoto || "",
        url: d.mapDoctorUrl || "#",
        fee: d.mapDoctorFee || "N/A",
        rating: d.mapDoctorRating || "",
        experience: d.mapDoctorExperience || ""
      }
    }
  }

  share(event) {
    event.preventDefault()
    event.stopPropagation()
    const shareUrl = event.currentTarget.dataset.shareUrl
    if (!shareUrl) {
      console.warn("No share URL found")
      return
    }

    const fullUrl = window.location.origin + shareUrl
    const shareTitle = event.currentTarget.dataset.shareTitle || (this.favoritableType === "Hospital" ? "Medifyr Hospital Profile" : "Medifyr Doctor Profile")
    const shareText = event.currentTarget.dataset.shareText || "Check out this profile"

    const originalHtml = event.currentTarget.innerHTML

    const showSuccessFeedback = () => {
      event.currentTarget.innerHTML = `
        <svg class="h-4 w-4 text-secondary animate-bounce" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      `
      setTimeout(() => {
        event.currentTarget.innerHTML = originalHtml
      }, 1500)
    }

    const fallbackCopy = () => {
      const copyText = (text) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          return navigator.clipboard.writeText(text)
        } else {
          const textarea = document.createElement("textarea")
          textarea.value = text
          textarea.style.position = "fixed"
          textarea.style.top = "0"
          textarea.style.left = "0"
          textarea.style.width = "2em"
          textarea.style.height = "2em"
          textarea.style.padding = "0"
          textarea.style.border = "none"
          textarea.style.outline = "none"
          textarea.style.boxShadow = "none"
          textarea.style.background = "transparent"
          document.body.appendChild(textarea)
          textarea.focus()
          textarea.select()
          try {
            const successful = document.execCommand("copy")
            document.body.removeChild(textarea)
            if (successful) {
              return Promise.resolve()
            } else {
              return Promise.reject("copy command failed")
            }
          } catch (err) {
            document.body.removeChild(textarea)
            return Promise.reject(err)
          }
        }
      }

      copyText(fullUrl).then(() => {
        showSuccessFeedback()
      }).catch(err => {
        console.error("Failed to copy URL: ", err)
        alert((this.favoritableType === "Hospital" ? (window.MediFyrI18n?.t?.share_hospital_link || "Hospital profile link: ") : (window.MediFyrI18n?.t?.share_doctor_link || "Doctor profile link: ")) + fullUrl)
      })
    }

    if (navigator.share) {
      navigator.share({
        title: shareTitle,
        text: shareText,
        url: fullUrl
      }).then(() => {
        showSuccessFeedback()
      }).catch(err => {
        if (err.name !== 'AbortError') {
          console.warn("Native share failed, falling back to copy", err)
          fallbackCopy()
        }
      })
    } else {
      fallbackCopy()
    }
  }

  get signedIn() {
    return document.body.dataset.userSignedIn === "true"
  }

  get loginUrl() {
    return document.body.dataset.loginUrl || "/login"
  }

  csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]')
    return el ? el.content : ""
  }

  toggleBookmark(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.signedIn) {
      window.dispatchEvent(new CustomEvent("login:open", {
        detail: { returnTo: window.location.pathname + window.location.search }
      }))
      return
    }

    const willSave = !this._isFavorited

    this._isFavorited = willSave
    this.renderBookmark()
    if (willSave && this.hasBookmarkIconTarget) {
      this.bookmarkIconTarget.classList.add("scale-125")
      setTimeout(() => this.bookmarkIconTarget.classList.remove("scale-125"), 200)
    }

    const favoritableId = this.doctorId || this.hospitalId
    const favoritableType = this.favoritableType

    fetch("/favorites/toggle", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ favoritable_id: favoritableId, favoritable_type: favoritableType })
    })
      .then(res => {
        if (res.status === 401) {
          this._isFavorited = !willSave
          this.renderBookmark()
          window.dispatchEvent(new CustomEvent("login:open", {
            detail: { returnTo: window.location.pathname + window.location.search }
          }))
          return Promise.reject("unauthorized")
        }
        return res.ok ? res.json() : Promise.reject(res)
      })
      .then(data => {
        this._isFavorited = !!data.is_favorite
        this.renderBookmark()
      })
      .catch(() => {
        this._isFavorited = !willSave
        this.renderBookmark()
      })
  }

  toggleCompare(event) {
    event.preventDefault()
    event.stopPropagation()

    const currentId = this.doctorId || this.hospitalId
    const i18n = window.MediFyrI18n?.t
    const itemType = this.favoritableType === "Hospital"
      ? (i18n?.item_hospitals || "hospitals")
      : (i18n?.item_doctors || "doctors")

    let compareList = JSON.parse(localStorage.getItem(this.storageKey) || "[]")
    if (compareList.some(d => d.id === currentId)) {
      compareList = compareList.filter(d => d.id !== currentId)
    } else {
      if (compareList.length >= 3) {
        const template = i18n?.max_compare || "You can compare a maximum of 3 %{itemType} at a time."
        alert(template.replace("%{itemType}", itemType))
        return
      }
      compareList.push(this.compareData())
      if (this.hasCompareIconTarget) {
        this.compareIconTarget.classList.add("rotate-12")
        setTimeout(() => this.compareIconTarget.classList.remove("rotate-12"), 200)
      }
    }
    localStorage.setItem(this.storageKey, JSON.stringify(compareList))
    window.dispatchEvent(new CustomEvent("compare:updated"))
    this.updateCompareState()
  }

  updateBookmarkState() {
    this._isFavorited = this.element.dataset.favorited === "true"
    this.renderBookmark()
  }

  renderBookmark() {
    if (!this.hasBookmarkIconTarget) return
    if (this._isFavorited) {
      this.bookmarkIconTarget.setAttribute("fill", "currentColor")
      this.bookmarkIconTarget.classList.remove("text-muted")
      this.bookmarkIconTarget.classList.add("text-optional")
    } else {
      this.bookmarkIconTarget.setAttribute("fill", "none")
      this.bookmarkIconTarget.classList.remove("text-optional")
      this.bookmarkIconTarget.classList.add("text-muted")
    }
  }

  updateCompareState() {
    const currentId = this.doctorId || this.hospitalId
    if (!currentId || !this.hasCompareIconTarget) return
    const compareList = JSON.parse(localStorage.getItem(this.storageKey) || "[]")
    const isCompared = compareList.some(d => d.id === currentId)
    
    if (isCompared) {
      this.compareIconTarget.classList.remove("text-muted")
      this.compareIconTarget.classList.add("text-primary")
      if (this.hasCompareTextTarget) {
        this.compareTextTarget.innerText = window.MediFyrI18n?.t?.added || "Added"
        this.compareTextTarget.classList.remove("text-muted")
        this.compareTextTarget.classList.add("text-primary")
      }
    } else {
      this.compareIconTarget.classList.remove("text-primary")
      this.compareIconTarget.classList.add("text-muted")
      if (this.hasCompareTextTarget) {
        this.compareTextTarget.innerText = window.MediFyrI18n?.t?.compare || "Compare"
        this.compareTextTarget.classList.remove("text-primary")
        this.compareTextTarget.classList.add("text-muted")
      }
    }
  }
}
